import {
  all,
  put,
  takeEvery,
  call,
  select
} from 'redux-saga/effects'

import moment from 'moment'

import * as Api from 'api/contacts_management'
import * as AppActions from 'actions/app'
import * as Actions from 'actions/contact_management'
import * as Selectors from 'selectors'
import { downloadBlob } from 'utils/sagas'

import { ExcelMimeType } from 'static/constants'

export function* createDistributionList({ payload: { name, description, openAddDialog } }) {
  const i18n = yield select(Selectors.getI18n)
  try {
    const body = { name }

    if (description) {
      body.description = description
    }

    const result = yield call(Api.createDistributionList, body)

    if (result.message) {
      yield put(Actions.createDistributionListError())
      yield put(AppActions.showAppSnackbarMessage({
        text: i18n.get('distribution_list_name_exists'),
        variant: 'error'
      }))

      return
    }

    const shouldOpenAddDialog = openAddDialog || false

    if (shouldOpenAddDialog) {
      yield put(Actions.createDistributionListSuccess(result))
      yield put(Actions.setSelectedListTab('add_contacts'))
      yield put(Actions.openDistributionListDetails({ distributionListId: result.id }))
    } else {
      yield put(Actions.createDistributionListSuccess(result))
    }

    yield put(AppActions.showAppSnackbarMessage({
      text: `${i18n.get('new_distribution_list')} ${i18n.get('created')}`,
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.createDistributionListError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: error.response.message,
      variant: 'error'
    }))
  }
}

export function* deleteDistributionLists({ payload }) {
  const i18n = yield select(Selectors.getI18n)
  try {
    const result = yield call(Api.deleteDistributionLists, payload.distributionListsIds)

    const multipleDeleted = result.length > 1

    yield put(Actions.deleteDistributionListsSuccess(result))
    yield put(AppActions.showAppSnackbarMessage({
      text: `${i18n.get(multipleDeleted ? 'distribution_lists' : 'distribution_list')} ${i18n.get('deleted')}`,
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.deleteDistributionListsError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* updateDistributionList({ payload }) {
  const i18n = yield select(Selectors.getI18n)
  try {
    const result = yield call(Api.updateDistributionList, payload)

    if (result.message) {
      yield put(Actions.updateDistributionListError())
      yield put(AppActions.showAppSnackbarMessage({
        text: i18n.get('distribution_list_name_exists'),
        variant: 'error'
      }))

      return
    }

    yield put(Actions.updateDistributionListSuccess(result))
    yield put(AppActions.showAppSnackbarMessage({
      text: `${i18n.get('distribution_list')} ${i18n.get('updated')}`,
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.updateDistributionListError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* exportDistributionList({ payload: { distributionList } }) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const contacts = distributionList.get('contacts')

    const hasClientSupplier = contacts.some(c => c.get('supplier') === 'client')

    if (!hasClientSupplier) {
      yield put(Actions.openDistributionListExportUnavailableDialog())

      return
    }

    const body = { id: distributionList.get('id') }

    const result = yield call(Api.exportDistributionList, body)

    if (result.message) {
      yield put(Actions.exportDistributionListError())
      yield put(AppActions.showAppSnackbarMessage({
        text: i18n.get('failed'),
        variant: 'error'
      }))

      return
    }

    yield call(downloadBlob, result, `${distributionList.get('name')}.xlsx`, ExcelMimeType)

    yield put(Actions.exportDistributionListSuccess(result))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('distribution_list_exported'),
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.exportDistributionListError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* mergeDistributionLists({ payload: { name, deleteMergedLists } }) {
  const i18n = yield select(Selectors.getI18n)
  const selectedDistributionLists = yield select(Selectors.getCmDaSelectedDistributionLists)
  const distributionListsIds = selectedDistributionLists.map(dl => dl.get('id'))
  const contactIds = selectedDistributionLists.map(dl => dl.get('contacts').map(c => c.get('id'))).flatten().toSet()

  try {
    const body = {
      name,
      ids: distributionListsIds.toArray(),
      contact_ids: contactIds.toArray(),
      delete_merged_lists: deleteMergedLists
    }

    const result = yield call(Api.mergeDistributionLists, body)

    if (result.message) {
      yield put(Actions.mergeDistributionListsError())
      yield put(AppActions.showAppSnackbarMessage({
        text: i18n.get('distribution_list_name_exists'),
        variant: 'error'
      }))

      return
    }

    yield put(Actions.mergeDistributionListsSuccess(result))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('distribution_lists_merged'),
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.mergeDistributionListsError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: error.response.message,
      variant: 'error'
    }))
  }
}

export function* addContactsToDistributionList({ payload }) {
  const contactId = payload && typeof payload === 'object' ? payload.contactId : payload
  const i18n = yield select(Selectors.getI18n)
  const gridData = yield select(Selectors.getCmDgSettings)
  const ccdPlan = yield select(Selectors.getCDeskPlan)
  const data = yield select(Selectors.getCmDaDistributionListFormData)
  const selectedList = yield select(Selectors.getCmDaDistributionListDetailsDataList)
  const selectedContactsToAdd = yield select(Selectors.getCmDaDistributionListDetailsSelectedContactsToAdd)
  const selectedContactsToRemove = yield select(Selectors.getCmDaDistributionListDetailsSelectedContactsToRemove)

  const contacts = gridData.get('rowSelectionModel')
  const selectedLists = data.get('selectedLists')
  const contactLimit = ccdPlan.get('contactsPerDistListLimit')

  try {
    if (contactId) {
      const body = {
        ids: [selectedList.get('id')],
        contact_ids: [contactId]
      }

      const result = yield call(Api.addContactsToDistributionLists, body)
      yield put(Actions.addContactsToDistributionListSuccess({ result, contactIds: [contactId] }))
      yield put(
        AppActions.showAppSnackbarMessage({
          text: i18n.get('added_to_distribution_list'),
          variant: 'success'
        })
      )

      return
    }

    if (selectedList.get('id') && !selectedContactsToAdd.isEmpty()) {
      const contactIds = selectedContactsToAdd.map(c => c.get('id'))

      if (contactIds.size > contactLimit) {
        yield put(Actions.addContactsToDistributionListError())
        yield put(
          AppActions.showAppSnackbarMessage({
            text: i18n.get('contacts_per_dist_list_limit_reached'),
            variant: 'error'
          })
        )

        return
      }

      const body = {
        ids: [selectedList.get('id')],
        contact_ids: contactIds
      }

      const result = yield call(Api.addContactsToDistributionLists, body)
      yield put(Actions.addContactsToDistributionListSuccess({ result, contactIds }))
      yield put(
        AppActions.showAppSnackbarMessage({
          text: i18n.get('added_to_distribution_list'),
          variant: 'success'
        })
      )

      return
    }

    if (selectedList.get('id') && !selectedContactsToRemove.isEmpty()) {
      const contactIds = selectedContactsToRemove.map(c => c.get('id'))

      if (contactIds.size > contactLimit) {
        yield put(Actions.addContactsToDistributionListError())
        yield put(
          AppActions.showAppSnackbarMessage({
            text: i18n.get('contacts_per_dist_list_limit_reached'),
            variant: 'error'
          })
        )

        return
      }

      const newListNames = data.get('newListNames')
      const body = {
        contact_ids: contactIds,
        distribution_list_ids: selectedLists,
        new_distribution_lists: newListNames
      }

      const result = yield call(Api.createListAndAddContacts, body)

      yield put(Actions.addContactsToDistributionListSuccess({ result, contactIds }))
      yield put(
        AppActions.showAppSnackbarMessage({
          text: i18n.get('added_to_distribution_list'),
          variant: 'success'
        })
      )

      return
    }

    if (contacts.size > contactLimit) {
      yield put(Actions.addContactsToDistributionListError())
      yield put(
        AppActions.showAppSnackbarMessage({
          text: i18n.get('contacts_per_dist_list_limit_reached'),
          variant: 'error'
        })
      )

      return
    }

    const newListNames = data.get('newListNames')
    const body = {
      contact_ids: contacts,
      distribution_list_ids: selectedLists,
      new_distribution_lists: newListNames
    }

    const result = yield call(Api.createListAndAddContacts, body)

    yield put(Actions.addContactsToDistributionListSuccess({ result, contactIds: contacts }))
    yield put(
      AppActions.showAppSnackbarMessage({
        text: i18n.get('added_to_distribution_list'),
        variant: 'success'
      })
    )
  } catch (error) {
    yield put(Actions.addContactsToDistributionListError(error))
    yield put(
      AppActions.showAppSnackbarMessage({
        text: error.response.message,
        variant: 'error'
      })
    )
  }
}

export function* removeContactsFromList() {
  const i18n = yield select(Selectors.getI18n)
  const listData = yield select(Selectors.getCmDaDistributionListDetailsDataList)
  const selectedContacts = yield select(Selectors.getCmDaDistributionListDetailsSelectedContactsToRemove)
  const contactsToRemove = yield select(Selectors.getCmDaContactsToRemove)

  const id = listData.get('id')
  const contactIds = contactsToRemove.isEmpty() ? selectedContacts.map(el => el.get('id')) : contactsToRemove.map(el => el.get('id'))

  const body = {
    contact_ids: contactIds
  }

  let result

  try {
    result = yield call(
      Api.removeContactsFromList,
      id,
      body
    )

    yield put(Actions.removeContactsFromListSuccess({ result, contactIds }))
    yield put(AppActions.showAppSnackbarMessage({
      text: `${contactIds.size > 1 ? i18n.get('contacts') : i18n.get('contact')} ${i18n.get('removed')}`,
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.removeContactsFromListError(error))
    yield put(
      AppActions.showAppMessage({
        text: i18n.get('failed'),
        success: false
      })
    )
  }
}

export function* removeContactsFromDistributionLists({ payload: distributionListIds }) {
  const i18n = yield select(Selectors.getI18n)
  const selectedContactIds = yield select(Selectors.getCmDgSettings)
  const contactIds = selectedContactIds.get('rowSelectionModel')

  try {
    const result = yield call(Api.removeContactsFromDistributionLists, {
      ids: distributionListIds,
      contact_ids: contactIds
    })

    yield put(Actions.removeContactsFromDistributionListsSuccess({
      contactIds,
      lists: result.lists
    }))
    yield put(AppActions.showAppSnackbarMessage({
      text: `${contactIds.size > 1 ? i18n.get('contacts') : i18n.get('contact')} ${i18n.get('removed')}`,
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.removeContactsFromDistributionListsError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* deleteContacts({ payload: idsToDelete }) {
  const i18n = yield select(Selectors.getI18n)
  try {
    const multipleDeleted = idsToDelete.length > 1
    yield call(Api.deleteContacts, idsToDelete)
    yield put(Actions.deleteContactsSuccess(idsToDelete))
    yield put(AppActions.showAppSnackbarMessage({
      text: `${i18n.get(multipleDeleted ? 'contacts' : 'contact')} ${i18n.get('deleted')}`,
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.deleteContactsError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* exportContacts({ payload: contactIds }) {
  const i18n = yield select(Selectors.getI18n)

  const body = {
    contact_ids: contactIds,
    sheet_name: i18n.get('contacts')
  }

  try {
    const result = yield call(Api.exportContacts, body)

    yield call(downloadBlob, result, `ContactManagement_Export_${moment().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`, ExcelMimeType)

    yield put(Actions.exportContactsSuccess(result))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('ccd_contacts_exported'),
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.exportContactsError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

// taker functions

export function* watchCreateDistributionListStart() {
  yield takeEvery(Actions.createDistributionListStart, createDistributionList)
}

export function* watchDeleteDistributionListsStart() {
  yield takeEvery(Actions.deleteDistributionListsStart, deleteDistributionLists)
}

export function* watchUpdateDistributionListStart() {
  yield takeEvery(Actions.updateDistributionListStart, updateDistributionList)
}

export function* watchExportDistributionListStart() {
  yield takeEvery(Actions.exportDistributionListStart, exportDistributionList)
}

export function* watchMergeDistributionListsStart() {
  yield takeEvery(Actions.mergeDistributionListsStart, mergeDistributionLists)
}

export function* watchAddContactsToDistributionListStart() {
  yield takeEvery(Actions.addContactsToDistributionListStart, addContactsToDistributionList)
}

export function* watchRemoveContactsFromListStart() {
  yield takeEvery(Actions.removeContactsFromListStart, removeContactsFromList)
}

export function* watchRemoveContactsFromDistributionListsStart() {
  yield takeEvery(Actions.removeContactsFromDistributionListsStart, removeContactsFromDistributionLists)
}
export function* watchDeleteContactsStart() {
  yield takeEvery(Actions.deleteContactsStart, deleteContacts)
}

export function* watchExportContactsStart() {
  yield takeEvery(Actions.exportContactsStart, exportContacts)
}

export default function* mainSaga() {
  yield all([
    watchCreateDistributionListStart(),
    watchDeleteDistributionListsStart(),
    watchUpdateDistributionListStart(),
    watchExportDistributionListStart(),
    watchMergeDistributionListsStart(),
    watchRemoveContactsFromDistributionListsStart(),
    watchDeleteContactsStart(),
    watchAddContactsToDistributionListStart(),
    watchRemoveContactsFromListStart(),
    watchExportContactsStart()
  ])
}
